import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { USER_DETAIL } from "@/constants/testIds";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp, RefreshCw } from "lucide-react";

const PH_INDIGO = "#4F46E5";  // CPS V2 (latest)
const PH_SLATE = "#94A3B8";   // Legacy CPS

const RANGES = [
  { key: "20", label: "20", limit: 20 },
  { key: "50", label: "50", limit: 50 },
  { key: "100", label: "100", limit: 100 },
];

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function TooltipCard({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const legacy = payload.find((p) => p.dataKey === "legacy");
  const v2 = payload.find((p) => p.dataKey === "cps_v2");
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-md p-2 text-[11px]">
      <div className="font-semibold text-slate-700 mb-1">{label}</div>
      {v2 && (
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: PH_INDIGO }} />
          <span className="text-slate-500">Latest (V2):</span>
          <span className="font-semibold text-slate-900 tabular-nums">
            {v2.value ?? "—"}
          </span>
        </div>
      )}
      {legacy && (
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: PH_SLATE }} />
          <span className="text-slate-500">Legacy:</span>
          <span className="font-semibold text-slate-900 tabular-nums">
            {legacy.value ?? "—"}
          </span>
        </div>
      )}
    </div>
  );
}

export default function CpsTrendChart({ userId, refreshKey }) {
  const [range, setRange] = useState("50");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const limit = RANGES.find((r) => r.key === range)?.limit || 50;
    let cancelled = false;
    setLoading(true);
    api.get(`/trial-users/${userId}/cps-v2-history?limit=${limit}`)
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.items || []);
        setErr(null);
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [userId, range, refreshKey]);

  const data = useMemo(() => {
    // Backend returns newest first → reverse for chronological x-axis
    return [...items].reverse().map((s) => ({
      t: fmtTime(s.created_at),
      legacy: typeof s.legacy_score === "number" ? s.legacy_score : null,
      cps_v2: typeof s.cps_v2 === "number" ? s.cps_v2 : null,
    }));
  }, [items]);

  const hasData = data.some((d) => d.legacy != null || d.cps_v2 != null);

  return (
    <div
      data-testid={USER_DETAIL.cpsTrendChart}
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3.5 w-3.5 text-indigo-600" />
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            CPS trend
          </div>
        </div>
        <div
          data-testid={USER_DETAIL.cpsTrendRange}
          className="inline-flex items-center gap-0.5 rounded-md border border-slate-200 p-0.5 bg-slate-50"
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`text-[10px] font-semibold px-2 py-0.5 rounded transition-colors duration-150 ${
                range === r.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-2 text-[10px]">
        <div className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: PH_INDIGO }} />
          <span className="text-slate-600 font-semibold">Latest (V2)</span>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: PH_SLATE }} />
          <span className="text-slate-600 font-semibold">Legacy</span>
        </div>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-xs text-slate-400">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="h-40 flex items-center justify-center text-xs text-red-500">
          {err}
        </div>
      ) : !hasData ? (
        <div className="h-40 flex items-center justify-center text-center text-xs text-slate-400 px-3">
          No score snapshots yet. Scores will appear here as the AI processes conversations.
        </div>
      ) : (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="t"
                tick={{ fontSize: 9, fill: "#64748B" }}
                tickLine={false}
                axisLine={{ stroke: "#E2E8F0" }}
                interval="preserveStartEnd"
                minTickGap={40}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 9, fill: "#64748B" }}
                tickLine={false}
                axisLine={{ stroke: "#E2E8F0" }}
                width={34}
              />
              <Tooltip content={<TooltipCard />} />
              <Line
                type="monotone"
                dataKey="cps_v2"
                stroke={PH_INDIGO}
                strokeWidth={2}
                dot={{ r: 2.5, fill: PH_INDIGO }}
                activeDot={{ r: 4 }}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="legacy"
                stroke={PH_SLATE}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={{ r: 2, fill: PH_SLATE }}
                activeDot={{ r: 3.5 }}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
        Shows last {RANGES.find((r) => r.key === range)?.limit} score snapshots. Newest on right.
      </p>
    </div>
  );
}
